import { FC, useMemo } from 'react';

import { usd } from '../data/format';

export interface IGraphCounterparty {
	name: string;
	country: string | null;
	entityType: string;
	transactions: number;
	volume: number;
	flagged: number;
}

/**
 * What the rules saw, as a graph: the subject in the middle, its counterparties
 * to the right, and any OTHER subject sharing one of its device fingerprints to
 * the left.
 *
 * Laid out deterministically — a fixed arc per side, ordered by transaction
 * count — rather than by a force simulation. A force layout re-settles on every
 * poll, so nodes drift under the cursor and the picture an analyst was reading
 * is not the picture a second later; and it needs a tick loop to draw a graph
 * that is almost always under twenty nodes.
 *
 * The left side is the point of the whole panel. A shared fingerprint is what
 * the multi-accounting rule scores, and the score alone does not tell you WHO
 * the other party is — this does.
 *
 * The two sides answer to DIFFERENT actions, which is why there are two
 * callbacks. A counterparty is a party this subject transacted WITH, so clicking
 * one narrows the evidence below to those transactions. A shared node is another
 * SUBJECT — `sharedWith` carries their `vendorData`, not a counterparty name —
 * so narrowing this subject's evidence by it would match nothing; clicking one
 * opens that subject instead. They used to look identical and only one of them
 * did anything.
 */
export const RelationshipGraph: FC<{
	subjectName: string;
	subjectVendorData: string;
	counterparties: IGraphCounterparty[];
	sharedWith: string[];
	selected: string | null;
	onSelect: (name: string | null) => void;
	onOpenSubject: (vendorData: string) => void;
}> = ({
	subjectName,
	subjectVendorData,
	counterparties,
	sharedWith,
	selected,
	onSelect,
	onOpenSubject,
}) => {
	const W = 620;
	const H = 300;
	const cx = W / 2;
	const cy = H / 2;

	/* Cap what is drawn. Past a dozen nodes an arc stops being readable and the
	   labels overlap; the tail is reachable in the counterparties table below,
	   which is the right place for a long list. */
	const cps = useMemo(
		() => counterparties.slice().sort((a, b) => b.transactions - a.transactions).slice(0, 9),
		[counterparties]
	);
	const shared = useMemo(() => sharedWith.slice(0, 5), [sharedWith]);

	const maxTx = Math.max(1, ...cps.map(c => c.transactions));

	/* Spread over an arc rather than a full circle so both sides read as
	   direction: counterparties out to the right, shared devices back to the left. */
	const place = (i: number, n: number, dir: 1 | -1) => {
		const spread = Math.min(2.0, 0.5 + n * 0.22);
		const t = n === 1 ? 0 : (i / (n - 1) - 0.5) * spread;
		const r = 200;
		return { x: cx + dir * Math.cos(t) * r, y: cy + Math.sin(t) * (H / 2.6) };
	};

	if (!cps.length && !shared.length) {
		return <div className="sv2-empty">No counterparties or shared devices for this subject.</div>;
	}

	return (
		<svg viewBox={`0 0 ${W} ${H}`} className="chart rgraph" role="img"
			aria-label={`Relationships for ${subjectName}`}>
			{/* edges first, so nodes paint over them */}
			{cps.map((c, i) => {
				const p = place(i, cps.length, 1);
				return (
					<line
						key={`e-${c.name}`}
						x1={cx}
						y1={cy}
						x2={p.x}
						y2={p.y}
						className={`rg-edge${c.flagged ? ' flagged' : ''}`}
						strokeWidth={1 + (c.transactions / maxTx) * 3}
					/>
				);
			})}
			{shared.map((v, i) => {
				const p = place(i, shared.length, -1);
				return <line key={`se-${v}`} x1={cx} y1={cy} x2={p.x} y2={p.y} className="rg-edge shared" />;
			})}

			{/* the subject */}
			<g
				className={`rg-node subject${selected === null ? ' on' : ''}`}
				onClick={() => onSelect(null)}
				role="button"
				tabIndex={0}
				onKeyDown={e => {
					if (e.key === 'Enter' || e.key === ' ') onSelect(null);
				}}
			>
				<title>{`${subjectName} · ${subjectVendorData} — click to clear the filter`}</title>
				<circle cx={cx} cy={cy} r={26} />
				<text x={cx} y={cy + 42} textAnchor="middle" className="rg-label">
					{subjectName.length > 22 ? `${subjectName.slice(0, 21)}…` : subjectName}
				</text>
			</g>

			{cps.map((c, i) => {
				const p = place(i, cps.length, 1);
				const on = selected === c.name;
				return (
					<g
						key={c.name}
						className={`rg-node cp${c.flagged ? ' flagged' : ''}${on ? ' on' : ''}`}
						onClick={() => onSelect(on ? null : c.name)}
						role="button"
						tabIndex={0}
						onKeyDown={e => {
							if (e.key === 'Enter' || e.key === ' ') onSelect(on ? null : c.name);
						}}
					>
						<title>
							{`${c.name} · ${c.country ?? 'unhosted'} · ${c.transactions} txns · ${usd(c.volume)}${
								c.flagged ? ` · ${c.flagged} flagged` : ''
							}`}
						</title>
						<circle cx={p.x} cy={p.y} r={13} />
						<text x={p.x} y={p.y - 20} textAnchor="middle" className="rg-label">
							{c.name.length > 18 ? `${c.name.slice(0, 17)}…` : c.name}
						</text>
					</g>
				);
			})}

			{shared.map((v, i) => {
				const p = place(i, shared.length, -1);
				return (
					<g
						key={v}
						className="rg-node shared"
						onClick={() => onOpenSubject(v)}
						role="button"
						tabIndex={0}
						onKeyDown={e => {
							if (e.key === 'Enter' || e.key === ' ') onOpenSubject(v);
						}}
					>
						<title>
							{`${v} — shares a device fingerprint with this subject; click to open them`}
						</title>
						<rect x={p.x - 13} y={p.y - 13} width={26} height={26} rx={5} />
						<text x={p.x} y={p.y - 20} textAnchor="middle" className="rg-label">
							{v}
						</text>
					</g>
				);
			})}
		</svg>
	);
};
